import {
  geoLocationValidator,
  type CoordinatesType,
} from "@validereinc/utilities";
import classNames from "classnames/bind";
import { isPlainObject } from "es-toolkit/compat";
import React, { useMemo } from "react";
import {
  useController,
  type FieldValues,
  type ValidateResult,
} from "react-hook-form";
import { mergeProps } from "../../utils/component-props";
import { InputWithLabels } from "../_components/InputWithLabels";
import TextInputBare from "../TextInput/TextInput";
import styles from "./GeoPointInput.module.scss";
import { ControlledGeoPointInputProps, GeoPointInputProps } from "./types";

const cx = classNames.bind(styles);

/**
 * Coordinate Inputs for longitude and latitude which accept either number or a proper coordinate like 43.6532° N
 * https://docs.google.com/document/d/1jTDCmd5LqmdMQpFtZfTTgXpa_7IieEMEEFbi2rdWMnw
 */
export const GeoPointInput = ({
  id,
  labelledById,
  name,
  value = ["", ""],
  onChange,
  onBlur,
  leftSlot,
  flairSlot,
  isFluid = false,
  isInline = false,
  isDisabled,
  shouldWrap,
  invalidMessage,
  "aria-invalid": ariaInvalid,
}: GeoPointInputProps) => {
  const getValidCoordinates = (coordinates: CoordinatesType) => {
    if (coordinates[0] === "" && coordinates[1] === "") {
      return;
    }

    return coordinates;
  };

  const handleChange = (coordinates: CoordinatesType) => {
    if (onChange) {
      onChange(getValidCoordinates(coordinates));
    }
  };

  const onLatitudeChange = (newValue: string) => {
    const newCoordinates: CoordinatesType = [newValue ?? "", value[1]];

    handleChange(newCoordinates);
  };

  const onLongitudeChange = (newValue: string) => {
    const newCoordinates: CoordinatesType = [value[0], newValue ?? ""];

    handleChange(newCoordinates);
  };

  return (
    <div className={cx("container", { isInline })}>
      {leftSlot && leftSlot({ className: cx("left-slot") })}

      <fieldset
        className={cx("input", {
          isFluid: isFluid,
          shouldWrap: shouldWrap,
        })}
        aria-labelledby={labelledById}
      >
        <TextInputBare
          id={`${id}-lat`}
          name={`latitude-input${name ? `-${name}` : ""}`}
          placeholder="Latitude"
          value={value[0]}
          onChange={onLatitudeChange}
          onBlur={onBlur}
          isDisabled={isDisabled}
          invalidMessage={invalidMessage}
          aria-invalid={ariaInvalid}
          flairSlot={flairSlot}
          className={cx("input-entry", {
            isFluid,
            isInline,
          })}
        />

        <TextInputBare
          id={`${id}-long`}
          name={`longitude-input${name ? `-${name}` : ""}`}
          placeholder="Longitude"
          value={value[1]}
          onChange={onLongitudeChange}
          onBlur={onBlur}
          isDisabled={isDisabled}
          invalidMessage={invalidMessage}
          aria-invalid={ariaInvalid}
          flairSlot={flairSlot}
          className={cx("input-entry", {
            isFluid,
            isInline,
          })}
        />
      </fieldset>
    </div>
  );
};

GeoPointInput.displayName = "GeoPointInput";

export const ControlledGeoPointInput = <T extends FieldValues>({
  name,
  defaultValue,
  rules,
  label,
  isLabelShown,
  labelPosition,
  description,
  isRequired,
  isOptionalTextShown,
  shouldShowDescriptionInTooltip,
  isInline = false,
  isFluid = true,
  ...restProps
}: ControlledGeoPointInputProps<T>) => {
  const { isDisabled, isLoading, isBusy } = restProps;
  const { field, fieldState } = useController({
    name,
    defaultValue,
    rules: {
      ...rules,
      validate:
        rules?.validate && typeof rules.validate === "function"
          ? rules.validate
          : {
              isInRange: (newValue: CoordinatesType) => {
                // if the underlying coordinate has missing data, don't run any validation
                if (
                  // if the field is optional, and the underlying geo-point isn't provided
                  (!isRequired && !newValue) ||
                  // user is interacting with it and hasn't completed entering the geo-point in its entirety
                  (!isRequired &&
                    newValue &&
                    Array.isArray(newValue) &&
                    newValue[0] === "" &&
                    newValue[1] === "")
                ) {
                  return;
                }

                const geoLocationValidationError =
                  geoLocationValidator(newValue);

                return geoLocationValidationError
                  ? geoLocationValidationError
                  : true;
              },
              ...(isRequired
                ? {
                    isRequired: (value: string): ValidateResult =>
                      [null, undefined, ""].includes(value) ||
                      (Array.isArray(value) &&
                        // eslint-disable-next-line @typescript-eslint/prefer-string-starts-ends-with
                        (!value.length || value[0] === "" || value[1] === ""))
                        ? `${typeof label === "string" ? label : "Geocoordinate input"} is required`
                        : undefined,
                  }
                : {}),
              ...(rules?.validate && isPlainObject(rules.validate)
                ? rules.validate
                : {}),
            },
    },
  });

  const mergedProps = useMemo(() => {
    return mergeProps(restProps, {
      onChange: field.onChange,
      onBlur: field.onBlur,
    });
  }, [restProps, field.onChange, field.onBlur]);

  const invalidMessage = fieldState.invalid
    ? fieldState.error?.message ||
      `Input is invalid (${fieldState.error?.type})`
    : undefined;

  return (
    <InputWithLabels
      colorScheme="light"
      isDisabled={isDisabled ?? false}
      isLoading={isLoading ?? false}
      isBusy={isBusy ?? false}
      label={label}
      isLabelShown={isLabelShown}
      labelPosition={labelPosition}
      description={description}
      isRequired={isRequired}
      isOptionalTextShown={isOptionalTextShown}
      shouldShowDescriptionInTooltip={shouldShowDescriptionInTooltip}
      isInline={isInline}
      isFluid={isFluid}
      invalidMessage={invalidMessage}
    >
      {({ leftSlot, flairSlot, invalidMessageId, labelId, inputId }) => (
        <GeoPointInput
          id={inputId}
          labelledById={labelId}
          invalidMessage={invalidMessage}
          aria-invalid={fieldState.invalid}
          aria-describedby={invalidMessageId}
          {...(!isLabelShown && !description && typeof label === "string"
            ? { "aria-label": label }
            : {})}
          isInline={isInline}
          isFluid={isFluid}
          leftSlot={leftSlot}
          flairSlot={flairSlot}
          {...field}
          {...mergedProps}
        />
      )}
    </InputWithLabels>
  );
};

ControlledGeoPointInput.displayName = "ControlledGeoPointInput";

export default GeoPointInput;
